// @flow
import React, { Component } from 'react';
import CONST from 'app/common/lib/Const';
import i18n from 'app/common/lib/i18n';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import runValidators from 'app/common/react/components/growth/unauth/lib/ValidationUtils';
import SocialConnectAgeCollectionForm from 'app/common/react/components/growth/unauth/signup/SocialConnectAgeCollection/SocialConnectAgeCollectionForm';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { ContextLogFunc } from 'app/common/redux/actions/contextLog';
import type { FacebookSignupOptions } from 'app/packages/pinterest-flow-types/registration';
import UserRegistration from 'app/common/react/components/growth/unauth/signup/UserRegistration';
import withContextLog from 'app/common/lib/react/deprecated/withContextLog'; // eslint-disable-line pinterest/no-deprecated-imports
import { Age } from 'app/common/lib/react/validator';
import { compose } from 'redux';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import {
  SIGNUP_METHOD,
  type FBRegistrationCredentialsType,
} from 'app/packages/pinterest-authentication/registration';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';

const styles = {
  container: {
    margin: '0 auto',
    width: '268px',
  },
};

type Props = {
  container: string,
  contextLog: ContextLogFunc,
  // @todo (sophia) FBRegistrationCredentialsType is being used for all registration types everywhere,
  // update this when it's renamed in pinterest-authentication/registration.js
  registrationCredentials: FBRegistrationCredentialsType,
  loadingIndicatorCallback: (boolean) => void,
  nextUrlParam?: string,
  requestContext: RequestContextType,
  showSignupWelcomeLoadingFn: (boolean) => void,
  showResponseMessageModal?: (isSignup: boolean) => void,
};

type State = {
  age: string,
  error: ?string,
};

export class SocialConnectAgeCollection extends Component<Props, State> {
  static defaultProps = {
    container: 'undefined',
  };

  state = {
    age: '',
    error: '',
  };

  componentDidMount() {
    this.track('shown');
    this.focusInputField();
  }

  // @todo (sophia) FBRegistrationCredentialsType is being used for all registration types everywhere,
  // update this when it's renamed in pinterest-authentication/registration.js
  getViewParameter = (registrationCredentials: FBRegistrationCredentialsType) => {
    if (registrationCredentials.type === SIGNUP_METHOD.FACEBOOK) {
      return PCONST.thrift.ViewParameterType.SIGNUP_FACEBOOK;
    } else if (registrationCredentials.type === SIGNUP_METHOD.GOOGLE) {
      return PCONST.thrift.ViewParameterType.SIGNUP_GPLUS;
    } else if (registrationCredentials.type === SIGNUP_METHOD.GOOGLE_ONE_TAP) {
      return PCONST.thrift.ViewParameterType.SIGNUP_GOOGLE_ONE_TAP_MODAL;
    }
    return undefined;
  };

  track(action: string) {
    const { container, registrationCredentials } = this.props;
    const method = registrationCredentials ? registrationCredentials.type : 'undefined';

    trackRegisterAction(`unauth_web.age_collection_form.${action}.${method}.${container}`);
  }

  inputRef: null;

  focusInputField() {
    if (this.inputRef && this.inputRef.textfield) {
      this.inputRef.textfield.focus(); // HACK to access the textfield (<input>) element of a Gestalt TextField
    }
  }

  handleAgeChange = ({ value }: { value: string }) => {
    this.setState({
      age: value,
    });
  };

  handleSignupSuccess = () => {
    this.track('signup_success');
    this.props.contextLog(PCONST.thrift.EventType.SIGNUP_SUCCESS, {
      viewParameter: this.getViewParameter(this.props.registrationCredentials),
    });

    const { showResponseMessageModal } = this.props;
    if (showResponseMessageModal) {
      showResponseMessageModal(true);
    } else {
      loginUtil.handleSignupRedirect(this.props.nextUrlParam || window.location.pathname, true);
    }
  };

  handleSignupFailure = (error: { api_error_code?: number, message?: string }) => {
    this.track('signup_failure');
    if (this.props.loadingIndicatorCallback) {
      this.props.loadingIndicatorCallback(false);
    }
    if (this.props.showSignupWelcomeLoadingFn) {
      this.props.showSignupWelcomeLoadingFn(false);
    }

    this.props.contextLog(PCONST.thrift.EventType.SIGNUP_FAILURE, {
      viewParameter: this.getViewParameter(this.props.registrationCredentials),
    });

    let errorMessage =
      error.message ||
      i18n._(
        'Oops! Something went wrong… Click Continue to try again.',
        'Error during signup during age collection step',
      );

    if (error.api_error_code === CONST.API_ERROR_UNDERAGE_REGISTER_ATTEMPT) {
      errorMessage = i18n._(
        'Uh oh... it looks like you’re not old enough just yet.',
        'Underage error during signup',
      );
    }

    this.setState({ error: errorMessage });
  };

  validateAge(age: string) {
    return runValidators(age, [Age({ message: i18n._('Please enter a valid number.') })]);
  }

  handleSubmit = () => {
    const { age } = this.state;
    this.track(`submit_button_click.${age || 'undefined'}`);
    const {
      contextLog,
      loadingIndicatorCallback,
      registrationCredentials,
      requestContext,
      showSignupWelcomeLoadingFn,
      showResponseMessageModal,
    } = this.props;

    const error = this.validateAge(age || '');
    if (error) {
      this.setState({
        error,
      });
      return;
    }

    this.setState({ error: '' });

    let welcomeExperimentName;
    if (registrationCredentials.type === SIGNUP_METHOD.GOOGLE_ONE_TAP) {
      welcomeExperimentName = 'web_unauth_google_one_tap_welcome';
    } else if (registrationCredentials.type === SIGNUP_METHOD.GOOGLE) {
      welcomeExperimentName = 'web_unauth_gplus_welcome_v2';
    } else if (registrationCredentials.type === SIGNUP_METHOD.FACEBOOK) {
      welcomeExperimentName = 'web_unauth_fb_welcome_v2';
    }

    let inWelcomeExperiment = false;
    if (welcomeExperimentName) {
      // todo(sophia) We need to flush experiment triggering immediately (current experiment client default wraps
      // activate resource call in a queued resource call with 5 second delay).
      // https://paper.dropbox.com/doc/Web-Welcome-Loading-Screen-Experiments-Triggering-Issues--AMlvl51RMVYopimH6DjeJ8EzAg-BwroITTNabE6ckNqNRoqJ
      inWelcomeExperiment = new SingleTreatmentExp(requestContext, welcomeExperimentName).enabled();
      requestContext.experiments.flush();
    } else if (loadingIndicatorCallback) {
      loadingIndicatorCallback(true);
    }

    contextLog(PCONST.thrift.EventType.SIGNUP_ATTEMPT, {
      viewParameter: this.getViewParameter(registrationCredentials),
    });

    if (registrationCredentials.type === SIGNUP_METHOD.FACEBOOK) {
      this.track('attempt_signup');
      const userDataResourceOptions: FacebookSignupOptions = UserRegistration.buildFacebookRegistrationResourceOptions(
        registrationCredentials.userData,
      );
      const registerResourceOptions = {
        ...userDataResourceOptions,
        ...registrationCredentials.credentialsAndLoggingData,
        age,
      };
      UserRegistration.register(registerResourceOptions).then(
        this.handleSignupSuccess,
        this.handleSignupFailure,
      );
    } else if (
      registrationCredentials.type === SIGNUP_METHOD.GOOGLE ||
      registrationCredentials.type === SIGNUP_METHOD.GOOGLE_ONE_TAP
    ) {
      this.track('attempt_signup');
      UserRegistration.register({ ...registrationCredentials.credentialsAndLoggingData, age }).then(
        this.handleSignupSuccess,
        this.handleSignupFailure,
      );
    }
    if (!showResponseMessageModal && showSignupWelcomeLoadingFn && inWelcomeExperiment) {
      showSignupWelcomeLoadingFn(true);
    }
  };

  render() {
    const { age, error } = this.state;
    return (
      <div style={styles.container}>
        <SocialConnectAgeCollectionForm
          age={age}
          error={error}
          handleAgeChange={this.handleAgeChange}
          handleSubmit={this.handleSubmit}
          inputRef={(c) => {
            this.inputRef = c;
          }}
        />
      </div>
    );
  }
}

export default compose(
  withRequestContext,
  withContextLog,
)(SocialConnectAgeCollection);
